import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Box,
  Typography,
  Alert,
  IconButton,
  Divider,
  Autocomplete,
  Chip
} from '@mui/material';
import { Close, LocationOn, Label } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, GeoJSON, LayersControl, LayerGroup } from 'react-leaflet';
import L, { Icon, DivIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to handle map clicks for location selection
const MapClickHandler: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
  });
  return null;
};

// Component to fly to location when it changes
const MapFlyTo: React.FC<{ center: [number, number]; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();

  React.useEffect(() => {
    if (center && center[0] && center[1]) {
      map.flyTo(center, zoom, { duration: 1.5 });
    }
  }, [center[0], center[1], zoom, map]);

  return null;
};

interface AddStationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (stationData: any) => Promise<boolean>;
  initialLocation?: { lat: number; lng: number };
}

const AddStationDialog: React.FC<AddStationDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialLocation
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: initialLocation?.lat || '',
    longitude: initialLocation?.lng || '',
    elevation: '',
    owner: '',
    source: '',
    timezone: 'UTC',
    public: false,
    debug: false,
    notes: ''
  });
  const [tags, setTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [mapCenter, setMapCenter] = useState<[number, number]>([39.0742, 21.8243]); // Greece center (Thessaly)
  const [giscoBoundaries, setGiscoBoundaries] = useState<any>(null);
  const [giscoLabelPoints, setGiscoLabelPoints] = useState<any>(null);

  useEffect(() => {
    fetch('https://gisco-services.ec.europa.eu/distribution/v2/countries/geojson/CNTR_RG_20M_2024_4326.geojson')
      .then(res => res.json())
      .then(data => setGiscoBoundaries(data))
      .catch(err => console.warn('Failed to load GISCO boundaries:', err));

    fetch('https://gisco-services.ec.europa.eu/distribution/v2/countries/geojson/CNTR_LB_2024_4326.geojson')
      .then(res => res.json())
      .then(data => setGiscoLabelPoints(data))
      .catch(err => console.warn('Failed to load GISCO label points:', err));
  }, []);

  const giscoBoundaryStyle = {
    color: '#2c3e50', weight: 2, opacity: 0.8, fillColor: 'transparent', fillOpacity: 0,
  };

  const toponymLabels = React.useMemo(() => {
    if (giscoLabelPoints) {
      return giscoLabelPoints.features
        .filter((f: any) => f.properties?.NAME_ENGL && f.geometry?.coordinates)
        .map((feature: any) => ({
          name: feature.properties.NAME_ENGL,
          lat: feature.geometry.coordinates[1],
          lng: feature.geometry.coordinates[0],
        }));
    }
    return [];
  }, [giscoLabelPoints]);

  const [showGiscoBoundaries, setShowGiscoBoundaries] = useState(true);
  const [showGiscoToponyms, setShowGiscoToponyms] = useState(false);
  const [showOsm, setShowOsm] = useState(true);

  const GiscoLayerEvents = () => {
    const map = useMap();
    useEffect(() => {
      const onAdd = (e: any) => {
        if (e.name === 'GISCO Boundaries') setShowGiscoBoundaries(true);
        if (e.name === 'Official EC Toponyms') setShowGiscoToponyms(true);
        if (e.name === 'OpenStreetMap') setShowOsm(true);
      };
      const onRemove = (e: any) => {
        if (e.name === 'GISCO Boundaries') setShowGiscoBoundaries(false);
        if (e.name === 'Official EC Toponyms') setShowGiscoToponyms(false);
        if (e.name === 'OpenStreetMap') setShowOsm(false);
      };
      map.on('overlayadd', onAdd);
      map.on('overlayremove', onRemove);
      return () => { map.off('overlayadd', onAdd); map.off('overlayremove', onRemove); };
    }, [map]);
    return null;
  };

  const GiscoBaseOpacity = () => {
    const map = useMap();
    useEffect(() => {
      const giscoActive = showGiscoBoundaries || showGiscoToponyms;
      map.eachLayer((l: any) => {
        if (l.setOpacity && l._url && (l._url.includes('openstreetmap') || l._url.includes('cartocdn'))) {
          l.setOpacity(giscoActive ? 0.5 : 1);
        }
      });
    }, [showGiscoBoundaries, showGiscoToponyms, map]);
    return null;
  };

  const GiscoDisclaimer = () => {
    const map = useMap();
    useEffect(() => {
      const disclaimer = new L.Control({ position: 'bottomright' });
      disclaimer.onAdd = () => {
        const div = L.DomUtil.create('div', 'gisco-disclaimer');
        div.style.cssText = 'background:rgba(255,255,255,0.85);padding:4px 8px;font-size:10px;max-width:420px;line-height:1.3;color:#333;border-radius:3px;pointer-events:none;margin-right:10px;margin-bottom:5px;';
        div.innerHTML = 'The designations employed and the presentation of material on this map do not imply the expression of any opinion whatsoever concerning the legal status of any country, territory, city or area or of its authorities, or concerning the delimitation of its frontiers or boundaries.';
        return div;
      };
      disclaimer.addTo(map);
      return () => { disclaimer.remove(); };
    }, [map]);
    return null;
  };

  React.useEffect(() => {
    if (initialLocation) {
      setFormData(prev => ({
        ...prev,
        latitude: initialLocation.lat,
        longitude: initialLocation.lng
      }));
    }
  }, [initialLocation]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (error) setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Station name is required');
      return false;
    }
    if (!formData.latitude || !formData.longitude) {
      setError('Latitude and longitude are required');
      return false;
    }
    if (isNaN(Number(formData.latitude)) || isNaN(Number(formData.longitude))) {
      setError('Latitude and longitude must be valid numbers');
      return false;
    }
    if (Number(formData.latitude) < -90 || Number(formData.latitude) > 90) {
      setError('Latitude must be between -90 and 90');
      return false;
    }
    if (Number(formData.longitude) < -180 || Number(formData.longitude) > 180) {
      setError('Longitude must be between -180 and 180');
      return false;
    }
    if (formData.elevation && isNaN(Number(formData.elevation))) {
      setError('Elevation must be a valid number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const stationData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        location: {
          latitude: Number(formData.latitude),
          longitude: Number(formData.longitude)
        },
        elevation: formData.elevation && !isNaN(Number(formData.elevation)) ? Number(formData.elevation) : undefined,
        timezone: formData.timezone.trim() || undefined,
        source: formData.source || undefined,
        owner: formData.owner.trim() || undefined,
        public: formData.public,
        status: formData.debug ? 'debug' : 'active',
        notes: formData.notes.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined
      };

      const success = await onSubmit(stationData);
      if (success) {
        handleClose();
      }
    } catch (err: any) {
      // Check if it's a unique constraint error
      if (err.response?.data?.error_type === 'unique_constraint') {
        setError(err.response.data.detail || 'A station with this name and source already exists. Please choose a different name or source.');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError(err.message || 'Failed to create station');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      latitude: '',
      longitude: '',
      elevation: '',
      owner: '',
      source: '',
      timezone: 'UTC',
      public: false,
      debug: false,
      notes: ''
    });
    setTags([]);
    setError('');
    onClose();
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      return;
    }

    // Check if running on HTTP (not localhost)
    const isSecureContext = window.isSecureContext;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (!isSecureContext && !isLocalhost) {
      setError('Geolocation requires HTTPS. Please use a secure connection or localhost for testing.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setFormData(prev => ({
          ...prev,
          latitude: latitude.toString(),
          longitude: longitude.toString()
        }));
        setMapCenter([latitude, longitude]);
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMsg = 'Failed to get current location. ';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg += 'Permission denied. Please check: (1) Browser permissions - click the lock icon in address bar, (2) System location services are enabled, (3) No browser extensions are blocking location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg += 'Location unavailable. This may happen if: (1) WiFi is disabled on desktop computers, (2) You are using a VPN, (3) Location services are disabled in your system settings. Try entering coordinates manually.';
            break;
          case error.TIMEOUT:
            errorMsg += 'Location request timed out. This may happen with slow GPS signal or network issues. Try again or enter coordinates manually.';
            break;
          default:
            errorMsg += 'An unknown error occurred. Try entering coordinates manually.';
        }

        setError(errorMsg);
      },
      {
        enableHighAccuracy: false,  // Set to false for better compatibility
        timeout: 15000,  // Increased timeout
        maximumAge: 60000  // Allow cached position up to 1 minute old
      }
    );
  };

  const handleMapLocationSelect = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
  };

  const timezones = ['UTC', ...(Intl as any).supportedValuesOf('timeZone')];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">
            Add New Station
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Station Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
                placeholder="e.g., Central Park Weather Station"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Brief description of the station and its purpose"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <LocationOn sx={{ mr: 1 }} />
                Location
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="latitude"
                label="Latitude"
                type="number"
                value={formData.latitude}
                onChange={handleInputChange}
                fullWidth
                required
                inputProps={{ step: 'any', min: -90, max: 90 }}
                placeholder="40.7128"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="longitude"
                label="Longitude"
                type="number"
                value={formData.longitude}
                onChange={handleInputChange}
                fullWidth
                required
                inputProps={{ step: 'any', min: -180, max: 180 }}
                placeholder="-74.0060"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                onClick={getCurrentLocation}
                variant="outlined"
                size="small"
                startIcon={<LocationOn />}
              >
                Use Current Location
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Click on the map to select location
              </Typography>
              <Box sx={{ height: '300px', borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}>
                <MapContainer
                  center={mapCenter}
                  zoom={6}
                  style={{ height: '100%', width: '100%' }}
                  attributionControl={false}
                >
                  <GiscoLayerEvents />
                  <GiscoBaseOpacity />
                  <LayersControl position="topright">
                    <LayersControl.Overlay checked name="OpenStreetMap">
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        subdomains="abc"
                        maxZoom={19}
                      />
                    </LayersControl.Overlay>
                    <LayersControl.Overlay checked name="GISCO Boundaries">
                      <LayerGroup>
                        {giscoBoundaries && (
                          <GeoJSON data={giscoBoundaries} style={giscoBoundaryStyle} />
                        )}
                      </LayerGroup>
                    </LayersControl.Overlay>
                    <LayersControl.Overlay name="Official EC Toponyms">
                      <LayerGroup>
                        {toponymLabels.map((t: any) => (
                          <Marker key={t.name} position={[t.lat, t.lng]} interactive={false}
                            icon={new DivIcon({
                              className: 'gisco-toponym',
                              html: `<span style="font-size:14px;font-weight:700;color:#1a237e;text-shadow:-1px -1px 0 #fff,1px -1px 0 #fff,-1px 1px 0 #fff,1px 1px 0 #fff,0 -1px 0 #fff,0 1px 0 #fff,-1px 0 0 #fff,1px 0 0 #fff;white-space:nowrap;pointer-events:none;">${t.name}</span>`,
                              iconSize: [0, 0], iconAnchor: [0, 0],
                            })}
                          />
                        ))}
                      </LayerGroup>
                    </LayersControl.Overlay>
                  </LayersControl>
                  <GiscoDisclaimer />
                  <MapClickHandler onLocationSelect={handleMapLocationSelect} />
                  {formData.latitude && formData.longitude && (
                    <>
                      <MapFlyTo
                        center={[Number(formData.latitude), Number(formData.longitude)]}
                        zoom={13}
                      />
                      <Marker
                        position={[Number(formData.latitude), Number(formData.longitude)]}
                      />
                    </>
                  )}
                </MapContainer>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="elevation"
                label="Elevation (meters)"
                type="number"
                value={formData.elevation}
                onChange={handleInputChange}
                fullWidth
                placeholder="15.5"
              />
            </Grid>

            <Grid item xs={6}>
              <Autocomplete
                options={timezones}
                value={formData.timezone}
                onChange={(_, val) => setFormData({ ...formData, timezone: val || 'UTC' })}
                groupBy={(option) => option === 'UTC' ? 'Common' : option.split('/')[0]}
                renderInput={(params) => (
                  <TextField {...params} label="Timezone" placeholder="Search timezone..." />
                )}
                size="small"
                disableClearable
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="owner"
                label="Provider"
                value={formData.owner}
                onChange={handleInputChange}
                fullWidth
                placeholder="Organization or individual name"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="source"
                label="Source"
                value={formData.source}
                onChange={handleInputChange}
                fullWidth
                select
              >
                <MenuItem value="">
                  <em>Select source type</em>
                </MenuItem>
                <MenuItem value="HTTP">HTTP</MenuItem>
                <MenuItem value="MQTT">MQTT</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                value={formData.notes}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Additional notes or comments"
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={tags}
                onChange={(event, newValue) => {
                  setTags(newValue as string[]);
                }}
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Type and press Enter to add tags (e.g., Home, Work, Outdoor)"
                    helperText="Add tags to group and filter stations"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      name="public"
                      checked={formData.public}
                      onChange={handleInputChange}
                      color="primary"
                    />
                  }
                  label="Public Station"
                />
                <FormControlLabel
                  control={
                    <Switch
                      name="debug"
                      checked={formData.debug}
                      onChange={handleInputChange}
                      color="primary"
                    />
                  }
                  label="Debug Mode"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Station'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddStationDialog;